import { useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { CiMail, CiPhone, CiUser } from "react-icons/ci";
import { FaChevronDown, FaClipboardList } from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import {
    LuCheckCircle2,
    LuLoader2,
    LuShieldCheck,
} from "react-icons/lu";
import { SlPaperClip } from "react-icons/sl";
import SweetAlertService from "../../utils/sweetalert";
import { Turnstile } from "@marsidev/react-turnstile";

const JENIS_OPTIONS = [
    { value: "bug", label: "Laporkan Error" },
    { value: "saran", label: "Saran & Masukan" },
    { value: "layanan", label: "Pelayanan" },
    { value: "lainnya", label: "Lainnya" },
];

type ReportFormData = {
    name: string;
    email: string;
    phone: string;
    report_type: string;
    description: string;
    img_report?: FileList;
};

const DEFAULT_VALUES: ReportFormData = {
    name: "",
    email: "",
    phone: "",
    report_type: "",
    description: "",
};

export default function Content() {
    const {
        register,
        handleSubmit,
        watch,
        reset,
        clearErrors,
        formState: { errors, isSubmitting },
    } = useForm<ReportFormData>({
        defaultValues: DEFAULT_VALUES,
    });

    const [status, setStatus] = useState<"idle" | "success">("idle");
    const [ticketId, setTicketId] = useState("");
    const [submittedName, setSubmittedName] = useState("");
    const [fileInputKey, setFileInputKey] = useState(0);

    // Turnstile token
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

    const jenisValue = watch("report_type", "");
    const pesanValue = watch("description", "");
    const gambarValue = watch("img_report");

    const fileName = gambarValue?.item(0)?.name ?? "";

    /**
     * Submit laporan
     */
    const onSubmit = async (data: ReportFormData) => {
        // Cek Turnstile terlebih dahulu
        if (!turnstileToken) {
            SweetAlertService.chapchaAlert();
            return;
        }

        try {
            const formData = new FormData();

            /**
             * Upload file
             *
             * Frontend: gambar
             * Backend: img_report
             */
            if (data.img_report && data.img_report.length > 0) {
                formData.append("img_report", data.img_report[0]);
            }

            /**
             * Mapping field frontend -> backend
             */
            formData.append("name", data.name);
            formData.append("email", data.email);
            formData.append("phone", data.phone);
            formData.append("report_type", data.report_type);
            formData.append("description", data.description);

            /**
             * Turnstile
             */
            formData.append(
                "g-recaptcha-response",
                turnstileToken
            );

            /**
             * POST ke Laravel
             */
            const response = await axios.post(
                `${process.env.REACT_APP_API_ENDPOINT}/report/create`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const responseData = response.data;

            console.log("Response:", responseData);

            if (responseData.status === "success") {
                /**
                 * Ambil nomor tiket dari backend
                 */
                const ticket =
                    responseData.data?.kode_tiket ??
                    responseData.data?.ticket_id ??
                    responseData.data?.ticket ??
                    "";

                setTicketId(ticket);
                setSubmittedName(data.name);

                /**
                 * Alert sukses
                 */
                SweetAlertService.successAlert();

                /**
                 * Reset form
                 */
                reset(DEFAULT_VALUES);
                clearErrors();

                /**
                 * Reset Turnstile
                 */
                setTurnstileToken(null);

                /**
                 * Reset file input
                 */
                setFileInputKey((key) => key + 1);

                /**
                 * Tampilkan halaman sukses
                 */
                setStatus("success");
            }
        } catch (error: any) {
            console.log("Error submit report:", error);

            if (error.response?.data) {
                const responseData = error.response.data;

                /**
                 * ==========================
                 * VALIDASI FILE
                 * ==========================
                 */
                if (
                    responseData.data?.img_report?.includes(
                        "Image max size is 2048"
                    )
                ) {
                    SweetAlertService.maxFileSize();
                }

                /**
                 * ==========================
                 * VALIDASI MIME FILE
                 * ==========================
                 */
                else if (
                    responseData.data?.img_report?.includes(
                        "Image must be jpeg, png, jpg, or webp"
                    )
                ) {
                    SweetAlertService.mimesAlert();
                }

                /**
                 * ==========================
                 * VALIDASI EMAIL
                 * ==========================
                 */
                else if (
                    responseData.data?.email?.includes(
                        "The email field must be a valid email address."
                    )
                ) {
                    SweetAlertService.emailValidationAlert();
                }

                /**
                 * ==========================
                 * VALIDASI TURNSTILE
                 * ==========================
                 */
                else if (
                    error.response.status === 422 &&
                    (
                        responseData.message?.includes("Verifikasi") ||
                        responseData.error_codes
                    )
                ) {
                    SweetAlertService.chapchaAlert();

                    // Token kemungkinan sudah invalid
                    setTurnstileToken(null);
                }

                /**
                 * ==========================
                 * VALIDASI LARAVEL LAINNYA
                 * ==========================
                 */
                else if (error.response.status === 422) {
                    SweetAlertService.warningAlert();
                }

                /**
                 * ==========================
                 * ERROR SERVER / LAINNYA
                 * ==========================
                 */
                else {
                    SweetAlertService.errorAlert();
                }
            } else {
                SweetAlertService.errorAlert();
            }
        }
    };

    /**
     * Reset form
     */
    const resetForm = () => {
        reset(DEFAULT_VALUES);
        clearErrors();

        setFileInputKey((key) => key + 1);
        setStatus("idle");
        setTicketId("");
        setSubmittedName("");
        setTurnstileToken(null);
    };

    /**
     * Styling input
     */
    const inputClass = (hasError: boolean) =>
        `w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
            hasError
                ? "border-red-300"
                : "border-slate-300"
        }`;

    return (
        <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">

                {/* TOP LINE */}
                <div className="h-1.5 bg-amber-500" />

                {/* =========================
                    SUCCESS
                ========================== */}
                {status === "success" ? (
                    <div className="px-8 py-14 flex flex-col items-center text-center">

                        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
                            <LuCheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>

                        <h3 className="font-serif text-xl font-semibold text-slate-900 mb-2">
                            Laporan Terkirim
                        </h3>

                        <p className="text-sm text-slate-500 max-w-sm mb-1">
                            Terima kasih, {submittedName}. Laporan Anda
                            sudah kami terima dan akan ditindaklanjuti
                            oleh tim Customer Care.
                        </p>

                        <p className="text-sm text-slate-400 mb-7">
                            Nomor tiket:{" "}
                            <span className="font-mono text-slate-700">
                                {ticketId || "-"}
                            </span>
                        </p>

                        <button
                            onClick={resetForm}
                            className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
                        >
                            Kirim laporan baru
                        </button>
                    </div>
                ) : (
                    <>
                        {/* =========================
                            HEADER
                        ========================== */}
                        <div className="flex items-start gap-4 px-8 pt-7 pb-5 border-b border-slate-100">

                            <div className="w-11 h-11 rounded-full bg-slate-900 flex items-center justify-center shrink-0">
                                <LuShieldCheck className="w-5 h-5 text-amber-400" />
                            </div>

                            <div>
                                <h2 className="font-serif text-xl font-semibold text-slate-900">
                                    Laporkan Masalah atau Saran
                                </h2>

                                <p className="text-sm text-slate-500 mt-1">
                                    Sampaikan keluhan, kendala transaksi,
                                    atau saran Anda. Tim Customer Care kami
                                    menindaklanjuti setiap laporan dalam
                                    1x24 jam kerja.
                                </p>
                            </div>
                        </div>

                        {/* =========================
                            FORM
                        ========================== */}
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="px-8 py-6 space-y-4"
                        >

                            {/* NAMA & EMAIL */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                {/* NAMA */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Nama Anda{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <div className="relative">
                                        <CiUser className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />

                                        <input
                                            type="text"
                                            placeholder="Masukkan nama Anda"
                                            className={inputClass(
                                                !!errors.name
                                            )}
                                            {...register("name", {
                                                required:
                                                    "Nama wajib diisi",
                                            })}
                                        />
                                    </div>

                                    {errors.name && (
                                        <p className="mt-1 text-xs text-red-500">
                                            {errors.name.message}
                                        </p>
                                    )}
                                </div>

                                {/* EMAIL */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Email Anda{" "}
                                    </label>

                                    <div className="relative">
                                        <CiMail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />

                                        <input
                                            type="email"
                                            placeholder="Masukkan email Anda"
                                            className={inputClass(
                                                !!errors.email
                                            )}
                                            {...register("email", {
                                                pattern: {
                                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                    message:
                                                        "Format email tidak valid",
                                                },
                                            })}
                                        />
                                    </div>

                                    {errors.email && (
                                        <p className="mt-1 text-xs text-red-500">
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* PHONE & JENIS */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                {/* PHONE */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Nomor Handphone{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <div className="relative">
                                        <CiPhone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />

                                        <input
                                            type="tel"
                                            placeholder="Masukkan nomor handphone"
                                            className={inputClass(
                                                !!errors.phone
                                            )}
                                            {...register("phone", {
                                                required:
                                                    "Nomor handphone wajib diisi",
                                                pattern: {
                                                    value: /^[0-9]*$/,
                                                    message:
                                                        "Nomor handphone harus berupa angka",
                                                },
                                            })}
                                            onKeyPress={(e) => {
                                                if (!/[0-9]/.test(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }}
                                        />
                                    </div>

                                    {errors.phone && (
                                        <p className="mt-1 text-xs text-red-500">
                                            {errors.phone.message}
                                        </p>
                                    )}
                                </div>

                                {/* JENIS */}
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                        Jenis Laporan{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <div className="relative">
                                        <FaClipboardList className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />

                                        <select
                                            className={`appearance-none ${inputClass(
                                                !!errors.report_type
                                            )} pr-9 ${
                                                jenisValue
                                                    ? "text-slate-900"
                                                    : "text-slate-400"
                                            }`}
                                            {...register("report_type", {
                                                required:
                                                    "Pilih jenis laporan",
                                            })}
                                        >
                                            <option value="" disabled>
                                                Pilih jenis laporan
                                            </option>

                                            {JENIS_OPTIONS.map((opt) => (
                                                <option
                                                    key={opt.value}
                                                    value={opt.value}
                                                    className="text-slate-900"
                                                >
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>

                                        <FaChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                    </div>

                                    {errors.report_type && (
                                        <p className="mt-1 text-xs text-red-500">
                                            {errors.report_type.message}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* GAMBAR */}
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                    Gambar{" "}
                                    <span className="text-slate-400 font-normal">
                                        (opsional)
                                    </span>
                                </label>

                                <label
                                    htmlFor="file-upload"
                                    className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 cursor-pointer hover:border-amber-400 hover:bg-amber-50 transition-colors"
                                >
                                    <span className="flex items-center gap-2 text-sm text-slate-600 truncate">

                                        <SlPaperClip className="w-4 h-4 text-slate-400 shrink-0" />

                                        <span className="truncate">
                                            {fileName ||
                                                "Belum ada file dipilih"}
                                        </span>

                                    </span>

                                    <span className="text-xs font-medium text-slate-900 bg-white border border-slate-300 rounded-md px-3 py-1.5 shrink-0">
                                        Pilih File
                                    </span>
                                </label>

                                <input
                                    key={fileInputKey}
                                    id="file-upload"
                                    type="file"
                                    accept="image/jpeg,image/png,image/jpg,image/webp"
                                    className="hidden"
                                    {...register("img_report")}
                                />

                                <p className="mt-1 text-xs text-slate-400">
                                    Format JPG, PNG, atau WEBP. Maks 2MB.
                                </p>
                            </div>

                            {/* PESAN */}
                            <div>
                                <div className="flex items-center justify-between mb-1.5">

                                    <label className="block text-sm font-medium text-slate-700">
                                        Pesan{" "}
                                        <span className="text-red-500">
                                            *
                                        </span>
                                    </label>

                                    <span className="text-xs text-slate-400">
                                        {pesanValue.length}/500
                                    </span>

                                </div>

                                <textarea
                                    rows={4}
                                    maxLength={500}
                                    placeholder="Tulis laporan atau saran Anda secara detail..."
                                    className={`w-full px-3.5 py-2.5 rounded-lg border text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                                        errors.description
                                            ? "border-red-300"
                                            : "border-slate-300"
                                    }`}
                                    {...register("description", {
                                        required:
                                            "Pesan wajib diisi",
                                        maxLength: {
                                            value: 500,
                                            message:
                                                "Maksimal 500 karakter",
                                        },
                                    })}
                                />

                                {errors.description && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {errors.description.message}
                                    </p>
                                )}
                            </div>

                            {/* TURNSTILE */}
                            <div>
                                <div className="flex justify-center rounded-lg border border-slate-200 bg-white px-4 py-3">

                                    <Turnstile
                                        siteKey="0x4AAAAAAELyvSMdGUHqlu0F"
                                        onSuccess={(token) => {
                                            setTurnstileToken(token);
                                        }}
                                        onExpire={() => {
                                            setTurnstileToken(null);
                                        }}
                                        onError={() => {
                                            setTurnstileToken(null);
                                        }}
                                    />

                                </div>
                            </div>

                            {/* BUTTON */}
                            <div className="flex items-center justify-end gap-3 pt-2">

                                <button
                                    type="button"
                                    onClick={resetForm}
                                    disabled={isSubmitting}
                                    className="px-5 py-2.5 rounded-lg text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
                                >
                                    Batal
                                </button>

                                <button
                                    type="submit"
                                    disabled={
                                        isSubmitting ||
                                        !turnstileToken
                                    }
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 transition-colors"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <LuLoader2 className="w-4 h-4 animate-spin" />
                                            Mengirim...
                                        </>
                                    ) : (
                                        <>
                                            <FiSend className="w-4 h-4" />
                                            Kirim Laporan
                                        </>
                                    )}
                                </button>

                            </div>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}